import { useMutation } from '@apollo/client/react';
import { usePolling } from '@optools/hooks/usePolling';
import { UPDATE_BROADCAST_SOURCE } from '@optools/services/operator-gateway/btc/mutations';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import {
  MediaFeedType,
  ProgramSource,
  SourceType,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useRef } from 'react';
import { toast } from 'sonner';

export type BroadcastSourceChangeArgs = {
  active: boolean;
  broadcastSourceType: SourceType;
  checked: boolean;
};

export type UseBroadcastSourceUpdateOptions = {
  date: string;
  feedType: MediaFeedType;
  gamePk: number;
  pipelineId: number;
  programSource: ProgramSource;
};

export function useBroadcastSourceUpdate({
  date,
  feedType,
  gamePk,
  pipelineId,
  programSource,
}: UseBroadcastSourceUpdateOptions) {
  const programSourceRef = useRef(programSource);
  programSourceRef.current = programSource;

  /** Target `programSource` on the pipeline (pill) we wait for when the active segment is toggled */
  const pendingProgramSourceRef = useRef<ProgramSource | null>(null);
  const pendingBroadcastSegmentRef = useRef<SourceType | null>(null);

  const [updateBroadcastSource, { loading: updateBroadcastSourceLoading }] =
    useMutation(UPDATE_BROADCAST_SOURCE);

  const { isPolling, startPolling } = usePolling({
    interval: 2000,
    onMaxAttemptsReached: () => {
      toast.warning('Failed to synchronize active source', {
        description:
          'Please refresh the page to ensure that the active source has been updated correctly.',
        duration: Infinity,
      });
      pendingProgramSourceRef.current = programSourceRef.current;
    },
    onPollingCompleted: () => {
      toast.success('Broadcast Source Success', {
        description: `Updated ${pendingBroadcastSegmentRef.current} to ${pendingProgramSourceRef.current}`,
      });
    },
    query: BTC_GAME_PIPELINE,
    shouldStopPolling: ({ game }) => {
      const nextPipeline = game?.pipelines.find((p) => p.id === pipelineId);
      return nextPipeline?.programSource === pendingProgramSourceRef.current;
    },
    variables: { gamePk, scheduleDate: date },
  });

  const handleBroadcastSourceChange = ({
    active,
    broadcastSourceType,
    checked,
  }: BroadcastSourceChangeArgs) => {
    const newBroadcastSource = checked ? ProgramSource.BACKHAUL : ProgramSource.FRONTHAUL;

    if (active) {
      pendingProgramSourceRef.current = newBroadcastSource;
      pendingBroadcastSegmentRef.current = broadcastSourceType;
    }

    updateBroadcastSource({
      awaitRefetchQueries: true,
      onCompleted: () => {
        if (active) {
          startPolling();
          return;
        }
        toast.success('Broadcast Source Success', {
          description: `Updated ${broadcastSourceType} to ${newBroadcastSource}`,
        });
      },
      onError: () => {
        if (active) {
          pendingProgramSourceRef.current = programSourceRef.current;
        }
      },
      refetchQueries: [{ query: BTC_GAME_PIPELINE, variables: { gamePk, scheduleDate: date } }],
      variables: {
        feedType,
        gamePk,
        sourceUpdateInput: { overrideType: broadcastSourceType, source: newBroadcastSource },
      },
    });
  };

  return {
    handleBroadcastSourceChange,
    isBroadcastSourcePending: updateBroadcastSourceLoading || isPolling,
  };
}
